import { create } from 'zustand';

// 标签项
export interface TabItem {
  key: string; // 完整路径 (如 /apps/123/edit)
  title: string; // 显示标题
  closable: boolean; // 仪表盘不可关闭
}

// 最大标签数
const MAX_TABS = 20;

// 默认仪表盘标签
const DASHBOARD_TAB: TabItem = {
  key: '/dashboard',
  title: '仪表盘',
  closable: false,
};

// 路径 -> 标签标题映射
export function getTabTitle(pathname: string): string {
  if (pathname === '/dashboard') return '仪表盘';
  if (pathname === '/apps') return '应用列表';
  if (pathname === '/apps/create') return '创建应用';
  if (/^\/apps\/\d+\/edit$/.test(pathname)) return '编辑应用';
  if (/^\/apps\/\d+$/.test(pathname)) return '应用详情';
  if (pathname === '/credentials') return '凭证列表';
  if (pathname === '/card-types') return '卡类列表';
  if (/^\/card-types\/\d+\/edit$/.test(pathname)) return '编辑卡类';
  if (pathname === '/cards') return '卡密列表';
  if (pathname === '/card-usages') return '使用记录';
  if (pathname === '/agents') return '代理商列表';
  if (pathname === '/agent-purchases') return '购卡记录';
  if (pathname === '/remote-variables') return '变量列表';
  if (/^\/remote-variables\/\d+\/edit$/.test(pathname)) return '编辑变量';
  if (pathname === '/remote-functions') return '函数列表';
  if (/^\/remote-functions\/\d+\/edit$/.test(pathname)) return '编辑函数';
  if (pathname === '/feature-quotas') return '功能配额';
  if (/^\/feature-quotas\/\d+\/usages$/.test(pathname)) return '配额用量明细';
  if (pathname === '/feature-quota-logs') return '功能配额日志';
  if (pathname === '/end-users') return '用户列表';
  if (pathname === '/end-users/online') return '在线用户';
  if (pathname === '/audit-logs') return '审计日志';
  if (pathname === '/client-logs') return '客户端日志';
  if (pathname === '/payment-orders') return '订单记录';
  if (pathname === '/tickets') return '工单列表';
  if (/^\/tickets\/\d+$/.test(pathname)) return '工单详情';
  if (pathname === '/timed-features') return '时段扣点';
  if (/^\/timed-features\/\d+\/grants$/.test(pathname)) return '窗口授权记录';
  if (pathname === '/forwarding-features') return '转发功能';
  if (pathname === '/forwarding-records') return '转发记录';
  if (pathname === '/blacklist') return '黑名单';
  // 属性模板标签标题
  if (pathname === '/attribute-templates') return '属性模板';
  if (pathname === '/attribute-templates/new') return '新建属性模板';
  if (/^\/attribute-templates\/\d+\/edit$/.test(pathname)) return '编辑属性模板';
  return '未知页面';
}

interface TabsState {
  tabs: TabItem[];
  activeKey: string;
  // 添加标签（去重，超限自动淘汰���早可关闭标签）
  addTab: (pathname: string) => void;
  // 关闭标签，返回关闭当前标签后应跳转的路径（非当前则返回 null）
  removeTab: (key: string) => string | null;
  // 激活标签
  setActiveTab: (key: string) => void;
}

export const useTabStore = create<TabsState>((set, get) => ({
  tabs: [DASHBOARD_TAB],
  activeKey: '/dashboard',

  addTab: (pathname: string) => {
    const { tabs } = get();
    const exists = tabs.find((t) => t.key === pathname);
    if (exists) {
      // 已存在，仅激活
      set({ activeKey: pathname });
      return;
    }

    const title = getTabTitle(pathname);
    const newTab: TabItem = { key: pathname, title, closable: true };
    let newTabs = [...tabs, newTab];

    // 超出上限，淘汰最早的可关闭标签
    while (newTabs.length > MAX_TABS) {
      const idx = newTabs.findIndex((t) => t.closable && t.key !== pathname);
      if (idx === -1) break;
      newTabs.splice(idx, 1);
    }

    set({ tabs: newTabs, activeKey: pathname });
  },

  removeTab: (key: string) => {
    const { tabs, activeKey } = get();
    const idx = tabs.findIndex((t) => t.key === key);
    if (idx === -1) return null;

    const newTabs = tabs.filter((t) => t.key !== key);
    set({ tabs: newTabs });

    // 如果关闭的不是当前标签，无需跳转
    if (activeKey !== key) return null;

    // 关闭的是当前标签，激活相邻标签：优先右侧 -> 左侧 -> 仪表盘
    let nextKey: string;
    if (idx < newTabs.length) {
      nextKey = newTabs[idx].key;
    } else if (idx > 0) {
      nextKey = newTabs[idx - 1].key;
    } else {
      nextKey = '/dashboard';
    }
    set({ activeKey: nextKey });
    return nextKey;
  },

  setActiveTab: (key: string) => {
    set({ activeKey: key });
  },
}));
